unit DashboardItemWidget;

{
  Single layout-driven dashboard widget.
  Behaviour is selected by TDashboardItemLayout (from DashboardItemType).
}

interface

uses
  System.SysUtils,
  System.Classes,
  JS,
  Web,
  WEBLib.Controls,
  WEBLib.ExtCtrls,
  BaseDashboardItem,
  DashboardItemLayout;

type
  TDashboardItemWidget = class(TBaseDashboardItem)
  private
    FLayout: TDashboardItemLayout;
    FLayoutAssigned: Boolean;
    function UsesWideLflFormat: Boolean;
    function DataUsesWideLflFormat(const ADataJSON: string): Boolean;
    procedure CreateAllLflSummaryTableRows(const ASummaryData: string);
    procedure DisplayTableOnly;
    [async]
    procedure DisplayChartWithSummary; async;
    [async]
    procedure DisplayChartWithDrillDownCombo; async;
    [async]
    procedure DisplayLikeForLike; async;
    procedure DisplayChartHideSummary;
  protected
    procedure CreateComponents; override;
    [async]
    procedure DisplayData; async; override;
    [async]
    procedure NewSummarySelected; async; override;
    function GetChartHeight: integer; override;
    function GetSummaryRowCount: integer; override;
    function GetSummaryColCount: integer; override;
    function RequiresDrillDownSQL: Boolean; override;
    function ShouldRunSummarySQLOnComboChange: Boolean; override;
    function GetSummaryIterationCount: Integer; override;
    function ResolveSummaryColumnName(AFieldIndex, AIterationIndex: Integer): string; override;
    procedure PopulateComboRowTable(ARowIndex: Integer); override;
  public
    procedure ApplyLayout(const ALayout: TDashboardItemLayout);
    property Layout: TDashboardItemLayout read FLayout;
  end;

implementation

const
  cLFL_SUFFIX = '_XX';

{ TDashboardItemWidget }

procedure TDashboardItemWidget.ApplyLayout(const ALayout: TDashboardItemLayout);
begin
  FLayout := ALayout;
  FLayoutAssigned := True;
end;

procedure TDashboardItemWidget.CreateComponents;
var
  lColCount: Integer;
begin
  inherited;
  if not FLayoutAssigned then
    Exit;

  if FLayout.ChartHeight > 0 then
    CreateChart;

  // Table-only widgets used to create the grid at shell time; only do so when
  // columns are known so an empty config cannot abort shell creation.
  if FLayout.CreateSummaryTableOnInit then
  begin
    lColCount := GetSummaryColCount;
    if lColCount > 0 then
      CreateSummaryTable(GetSummaryRowCount, lColCount);
  end;
end;

procedure TDashboardItemWidget.DisplayTableOnly;
var
  lTableData: string;
begin
  RemoveSummaryCombo;

  lTableData := GetEffectiveChartJson;
  if HasChartData(lTableData) then
  begin
    EnsureSummaryFieldsReady(lTableData);
    PopulateSummaryTableFromJson(lTableData, GetSummaryRowCount);
  end
  else
    HideSummaryGridIfEmpty;
  ApplyLayoutSettings;
  HideDataSkeleton;
end;

procedure TDashboardItemWidget.DisplayChartWithSummary;
var
  lTableData: string;
  lChartSource: string;
begin
  RemoveSummaryCombo;

  lChartSource := GetEffectiveChartJson;

  if Trim(lChartSource) <> '' then
    lTableData := lChartSource
  else if Trim(SummaryData) <> '' then
    lTableData := SummaryData
  else
    lTableData := '';

  if HasChartData(lChartSource) then
    AddChartAndData(lChartSource)
  else
    ShowChartNoData;

  if IsTradingPatternWidget and HasChartData(lChartSource) then
    await(DisplayComboRowSummary)
  else if UsesComboRowSummary then
    await(DisplayComboRowSummary)
  else
  begin
    if ShouldShowFullSummaryTable and (Trim(lTableData) <> '') and (Trim(lTableData) <> '[]') then
      PopulateSummaryTableFromJson(lTableData, 0)
    else
      HideSummaryGridIfEmpty;
  end;

  ApplyLayoutSettings;
  HideDataSkeleton;
end;

procedure TDashboardItemWidget.DisplayChartWithDrillDownCombo;
begin
  RemoveSummaryCombo;
  if IsDrillDownComboEnabled then
    await(DisplayDrillDownWithCombo)
  else
    await(DisplayChartWithSummary);
end;

function TDashboardItemWidget.UsesWideLflFormat: Boolean;
begin
  if Length(Configuration.SummaryFields) = 0 then
    Result := False
  else
    Result := Pos(cLFL_SUFFIX, Configuration.SummaryFields[0].ToField) > 0;
end;

function TDashboardItemWidget.DataUsesWideLflFormat(const ADataJSON: string): Boolean;
var
  lSummaryArray: JS.TJSArray;
  lRowData: JS.TJSObject;
  lProbeField: string;
begin
  Result := UsesWideLflFormat;
  if not Result then
    Exit;

  if Trim(ADataJSON) = '' then
    Exit;

  lSummaryArray := JS.toArray(TJSJSON.parse(ADataJSON));
  if lSummaryArray.Length = 0 then
    Exit;

  lRowData := JS.toObject(lSummaryArray[0]);
  lProbeField := ResolveSummaryColumnName(0, 0);
  Result := string(TJSObject(lRowData)[lProbeField]) <> '';
end;

procedure TDashboardItemWidget.CreateAllLflSummaryTableRows(const ASummaryData: string);
var
  I, R, J, lTableRow: Integer;
  lFieldType: string;
  lSummaryArray: JS.TJSArray;
  lRowData: JS.TJSObject;
  lMaxDataRows: Integer;
begin
  if Trim(ASummaryData) = '' then
    Exit;

  lSummaryArray := JS.toArray(TJSJSON.parse(ASummaryData));
  if lSummaryArray.Length = 0 then
    Exit;

  lMaxDataRows := lSummaryArray.Length * GetSummaryIterationCount;
  if lMaxDataRows < GetChartSummaryVisibleRowCount then
    lMaxDataRows := GetChartSummaryVisibleRowCount;

  if not Assigned(SummaryGrid) then
    CreateSummaryTable(lMaxDataRows, GetSummaryColCount)
  else
  begin
    SummaryGrid.ColCount := GetSummaryColCount;
    SummaryGrid.RowCount := ResolveChartSummaryGridRowCount(lMaxDataRows);
  end;

  for I := 0 to Length(Configuration.SummaryFields) - 1 do
    SummaryGrid.Cells[I, 0] := Configuration.SummaryFields[I].DisplayName;

  for lTableRow := 1 to lMaxDataRows do
    for I := 0 to Length(Configuration.SummaryFields) - 1 do
      SummaryGrid.Cells[I, lTableRow] := '';

  lTableRow := 1;
  for R := 0 to lSummaryArray.Length - 1 do
  begin
    lRowData := JS.toObject(lSummaryArray[R]);
    for J := 0 to GetSummaryIterationCount - 1 do
    begin
      if lTableRow > lMaxDataRows then
        Break;
      for I := 0 to Length(Configuration.SummaryFields) - 1 do
      begin
        lFieldType := Configuration.SummaryFields[I].FieldType;
        SummaryGrid.Cells[I, lTableRow] := FormatSummaryCellValue(lFieldType,
          Configuration.SummaryFields[I].ToField,
          GetSummaryFieldValueAtIteration(lRowData, I, J));
      end;
      Inc(lTableRow);
    end;
    if lTableRow > lMaxDataRows then
      Break;
  end;

  SyncSummaryGridContentHeight;
  ApplySummaryTableLayout;
  ApplyWidgetTableHeaderStyles;
  ConfigureSummaryTableInteraction;
end;

procedure TDashboardItemWidget.DisplayLikeForLike;
var
  lChartSource: string;
begin
  RemoveSummaryCombo;

  lChartSource := GetEffectiveChartJson;
  if HasChartData(lChartSource) then
  begin
    AddChartAndData(lChartSource);
    if UsesComboRowSummary then
      await(DisplayComboRowSummary)
    else if ShouldShowFullSummaryTable then
    begin
      if DataUsesWideLflFormat(lChartSource) then
        CreateAllLflSummaryTableRows(lChartSource)
      else
        PopulateSummaryTableFromJson(lChartSource, 0);
    end
    else
      HideSummaryGridIfEmpty;
  end
  else
    ShowChartNoData;

  ApplyLayoutSettings;
  HideDataSkeleton;
end;

procedure TDashboardItemWidget.DisplayChartHideSummary;
var
  lChartSource: string;
begin
  RemoveSummaryCombo;

  lChartSource := GetEffectiveChartJson;
  if HasChartData(lChartSource) then
  begin
    AddChartAndData(lChartSource);
    HideSummaryGridIfEmpty;
    ApplyLayoutSettings;
    HideDataSkeleton;
  end
  else
    ShowChartNoData;
end;

procedure TDashboardItemWidget.DisplayData;
begin
  if not FLayoutAssigned then
  begin
    inherited DisplayData;
    Exit;
  end;

  case FLayout.DisplayMode of
    dmTableOnly:
      DisplayTableOnly;
    dmChartWithSummary:
      await(DisplayChartWithSummary);
    dmChartWithDrillDownCombo:
      await(DisplayChartWithDrillDownCombo);
    dmLikeForLike:
      await(DisplayLikeForLike);
    dmChartHideSummary:
      DisplayChartHideSummary;
  else
    await(DisplayChartWithSummary);
  end;
end;

procedure TDashboardItemWidget.NewSummarySelected;
begin
  if FLayoutAssigned and (FLayout.DisplayMode = dmChartHideSummary) then
    PopulateSummaryTableFromJson(ChartData, GetSummaryRowCount)
  else
    inherited NewSummarySelected;
end;

function TDashboardItemWidget.GetChartHeight: integer;
begin
  if FLayoutAssigned then
    Result := FLayout.ChartHeight
  else
    Result := 540;
end;

function TDashboardItemWidget.GetSummaryColCount: integer;
begin
  Result := SummaryFieldCount;
end;

function TDashboardItemWidget.GetSummaryRowCount: integer;
begin
  if FLayoutAssigned then
  begin
    if FLayout.DisplayMode = dmLikeForLike then
      Result := GetSummaryIterationCount
    else
      Result := FLayout.SummaryRowCount;
  end
  else
    Result := 1;
end;

function TDashboardItemWidget.RequiresDrillDownSQL: Boolean;
begin
  if not FLayoutAssigned then
    Exit(inherited RequiresDrillDownSQL);

  case FLayout.DrillDownMode of
    ddmAlways:
      Result := True;
    ddmTradingPatternTitle:
      // Daily chart JSON is TransDate + SELLNET only; Profit/Quantity/etc. come
      // from template SQLSummary drill-down. Hourly/Monthly embed metrics in rows.
      Result := SameText(Trim(Title), 'Daily Trading Pattern');
  else
    if FLayout.DisplayMode = dmLikeForLike then
      Result := False
    else
      Result := inherited RequiresDrillDownSQL;
  end;
end;

function TDashboardItemWidget.ShouldRunSummarySQLOnComboChange: Boolean;
begin
  if not FLayoutAssigned then
    Exit(inherited ShouldRunSummarySQLOnComboChange);
  if FLayout.DisplayMode = dmLikeForLike then
    Exit(False);
  if FLayout.RunSummarySQLOnComboChange then
    Exit(True);
  Result := RequiresDrillDownSQL;
end;

function TDashboardItemWidget.GetSummaryIterationCount: Integer;
begin
  if FLayoutAssigned and (FLayout.SummaryIterationCount > 1) then
    Result := FLayout.SummaryIterationCount
  else
    Result := inherited GetSummaryIterationCount;
end;

function TDashboardItemWidget.ResolveSummaryColumnName(AFieldIndex,
  AIterationIndex: Integer): string;
begin
  if FLayoutAssigned and (FLayout.DisplayMode = dmLikeForLike) then
    Result := StringReplace(Configuration.SummaryFields[AFieldIndex].ToField, cLFL_SUFFIX,
      Format('_%d', [AIterationIndex]), [])
  else
    Result := inherited ResolveSummaryColumnName(AFieldIndex, AIterationIndex);
end;

procedure TDashboardItemWidget.PopulateComboRowTable(ARowIndex: Integer);
var
  lRowJson: string;
begin
  if not (FLayoutAssigned and (FLayout.DisplayMode = dmLikeForLike)) then
  begin
    inherited PopulateComboRowTable(ARowIndex);
    Exit;
  end;

  if Length(Configuration.SummaryFields) = 0 then
    EnsureSummaryFieldsReady(GetEffectiveChartJson);
  if Length(Configuration.SummaryFields) = 0 then
    Exit;

  lRowJson := ExtractChartRowJson(GetEffectiveChartJson, ARowIndex);
  if DataUsesWideLflFormat(lRowJson) then
    CreateAllLflSummaryTableRows(lRowJson)
  else
    inherited PopulateComboRowTable(ARowIndex);
end;

end.
